import Table from 'components/commercetools-ui/atoms/table';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CT_STOREKEY } from 'helpers/constants/envVariables';
import { desktop } from 'helpers/utils/screensizes';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { localeMapping } from 'helpers/constants/localeMapping';
import { getPdpUrlWithLocale } from 'helpers/utils/pdpUrlHelper';

interface BundleAttributes {
  bundleData: any[] | undefined;
}

const BundleProducts: React.FC<BundleAttributes> = ({ bundleData }) => {
  const [isDesktop] = useMediaQuery(desktop);
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });

  const { locale } = useParams();
  const localeKey = localeMapping[locale];

  return (
    <div id="components-section" className="">
      {Array.isArray(bundleData) && bundleData.length > 0 && (
        <div className="px-24 pl-24 pr-24 pt-48 md:px-28 md:pl-28 lg:pl-30 lg:pr-48 lg:pt-48 xl:pl-0 xl:pr-60 xl:pt-80 2xl:pl-0 2xl:pr-80 2xl:pt-80">
          <Typography className="mb-20 text-32 !font-medium leading-8 text-[#090909] md:leading-[3.25rem] xl:text-36 2xl:text-48">
            {formatProductMessage({ id: 'product.components', defaultMessage: 'Components' })}
          </Typography>
        </div>
      )}
      <div className="px-24 pl-24 pr-24 md:px-28 md:pl-28 lg:pl-30 lg:pr-48 xl:pl-0 xl:pr-60 2xl:pl-0 2xl:pr-80 ">
        {!isDesktop ? (
          <>
            {Array.isArray(bundleData) && bundleData.length > 0 ? (
              bundleData.map((item) => (
                <div className="" key={item?.id}>
                  <Link
                    className="flex items-center gap-20"
                    href={getPdpUrlWithLocale(locale, item?.Slug[localeKey], item?.SKU, item?.Colour)}
                  >
                    <img
                      src={`${
                        item?.Images?.[0]?.url
                          ? item?.Images?.[0]?.url
                          : '/images/no-image-placeholder/product-thumbnail.jpg'
                      }`}
                      alt=""
                      className="my-10 w-80 cursor-pointer border-b border-white lg:w-60 2xl:w-80"
                    />
                    <div className="flex flex-col">
                      <div className="text-16 font-medium leading-4 text-[#090909] capitalize">{item?.Name[`${localeKey}`]}</div>
                      <div className="flex flex-col justify-between py-6">
                        <div className="mr-10">
                          <span style={{ color: '#666666' }} className="text-12 leading-4">
                            {formatProductMessage({ id: 'product.code', defaultMessage: 'Code' })}:
                          </span>
                          <span className="text-12">{item?.ItemCode}</span>
                        </div>
                        <div className="flex gap-1">
                          <span className="text-12 leading-4 text-[#666666]">
                            {formatProductMessage({ id: 'product.rrp.price', defaultMessage: 'RRP' })}{' '}
                            {formatProductMessage({ id: 'product.inc.tax', defaultMessage: '(inc. GST)' })}:
                          </span>
                          <span className="text-12 leading-4 ">
                            {/* Displaying component price based on country */}
                            {'$' +
                              (
                                (item?.Prices?.find((obj: { country: string }) => obj.country == locale.toUpperCase())
                                  ?.value?.centAmount as number) / 100
                              ).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                  <hr />
                </div>
              ))
            ) : (
              <div></div>
            )}
          </>
        ) : (
          <div className="">
            {Array.isArray(bundleData) && bundleData.length > 0 && (
              <div className="">
                <Table.Container className="max-w-full !border-[#CCCCCC]">
                  <Table.Head>
                    <Table.Row className="h-50 border-b px-6 py-4 text-left">
                      <Table.Cell> </Table.Cell>
                      <Table.Cell>
                        <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                          {formatProductMessage({ id: 'product.productname', defaultMessage: 'Product name' })}
                        </Typography>{' '}
                      </Table.Cell>
                      <Table.Cell>
                        <Typography className="text-16 !font-medium leading-6 text-[#090909] md:whitespace-nowrap xl:whitespace-normal">
                          {formatProductMessage({ id: 'product.code', defaultMessage: 'Code' })}
                        </Typography>
                      </Table.Cell>
                      <Table.Cell>
                        <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                          {formatProductMessage({ id: 'product.rrp.price', defaultMessage: 'RRP' })}
                        </Typography>
                      </Table.Cell>
                    </Table.Row>
                  </Table.Head>
                  <Table.Body>
                    {bundleData.map((item) => (
                      <>
                        <Link href={getPdpUrlWithLocale(locale, item?.Slug[localeKey], item?.SKU, item?.Colour)} className="contents">
                        
                          <Table.Row className="h-80 border-b border-gray-300 align-middle" key={item?.id}>
                            <Table.Cell className="!p-0 lg:w-80 2xl:w-100">
                              <Link href={getPdpUrlWithLocale(locale, item?.Slug[localeKey], item?.SKU, item?.Colour)}>
                                <img
                                  src={`${
                                    item?.Images?.[0]?.url
                                      ? item?.Images?.[0]?.url
                                      : '/images/no-image-placeholder/product-thumbnail.jpg'
                                  }`}
                                  alt=""
                                  className="cursor-pointer border-b border-white lg:w-60 2xl:w-80"
                                />
                              </Link>
                            </Table.Cell>
                            <Table.Cell className="whitespace-normal">
                              <Typography className="text-16 !font-medium leading-6 text-[#090909] capitalize">
                                {item?.Name?.[`${localeKey}`]}
                              </Typography>
                            </Table.Cell>
                            <Table.Cell>
                              <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                                {item?.ItemCode}
                              </Typography>
                            </Table.Cell>
                            <Table.Cell>
                              {/* Displaying component price based on country */}
                              <Typography className="text-16 !font-medium leading-6 text-[#090909]">
                                {'$' +
                                  (
                                    (item?.Prices?.find(
                                      (obj: { country: string }) => obj.country == locale.toUpperCase(),
                                    )?.value?.centAmount as number) / 100
                                  ).toFixed(2)}
                              </Typography>

                              <Typography as="span">
                                {formatProductMessage({ id: 'product.inc.tax', defaultMessage: '(inc. GST)' })}
                              </Typography>
                            </Table.Cell>
                          </Table.Row>
                        </Link>
                      </>
                    ))}
                  </Table.Body>
                </Table.Container>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BundleProducts;
